// A pot of popcorn on the heat, in toy space with the floor at y=0 and the pot
// centred on the y axis. Heat builds while the pot is heated and leaks away
// when it is not. Each kernel pops once the heat passes its own threshold
// (spread around a middle value, so popping starts slowly, peaks, then trails
// off), a moment later. The puffed corn jumps, bounces off the walls and the
// lid, and settles on a pile that grows by each puff's packed volume. The pile
// may dome over the rim; the lid rests on top of it and jumps at each hit.
// Corn landing where the dome is full rolls over the rim onto the floor.
export class PopcornPot{
  constructor({kernels,inner,outer,floorY,rimY,size=1,random=Math.random}){
    Object.assign(this,{inner,outer,floorY,rimY,size,random});
    this.start=kernels.map(p=>[...p]);
    this.n=5;this.cell=2*inner/this.n;
    this.reset();
  }
  reset(){
    const r=this.random;
    this.kernels=this.start.map(p=>{
      // Roughly normal thresholds around the middle of the heat range.
      const g=(r()+r()+r()+r()-2)/1.15;
      return {p:[...p],popped:false,threshold:Math.max(.42,Math.min(.92,.66+.13*g)),readyAt:-1};
    });
    this.puffs=[];this.heat=0;this.clock=0;this.lid=0;this.lidSpeed=0;
    this.pile=new Float64Array(this.n*this.n).fill(this.floorY);
  }
  bin(x,z){const n=this.n,i=Math.max(0,Math.min(n-1,Math.floor((x+this.inner)/this.cell))),j=Math.max(0,Math.min(n-1,Math.floor((z+this.inner)/this.cell)));return i*n+j;}
  pileAt(x,z){return this.pile[this.bin(x,z)];}
  // The pile may dome above the rim, highest in the middle.
  limit(d){return this.rimY+this.size*.35*Math.max(0,1-d/this.inner);}
  get top(){let m=this.floorY;for(const h of this.pile)m=Math.max(m,h);return m;}
  pop(k){
    const r=this.random,R=this.size;k.popped=true;
    this.puffs.push({p:[k.p[0],k.p[1]+.02*R,k.p[2]],v:[(r()-.5)*.9*R,(1.6+.8*r())*R,(r()-.5)*.9*R],size:R*(.15+.06*r()),spin:(r()-.5)*14,
      rest:false,spilling:false,pink:r()<.06,turn:r()*Math.PI*2});
  }
  settle(f,h){
    const R=this.size,n=this.n,b=this.bin(f.p[0],f.p[2]),i=Math.floor(b/n),j=b%n;
    f.p[1]=h+f.size*.7;f.rest=true;f.v=[0,0,0];
    // The pile grows by the puff's packed volume, mostly in its own cell.
    const grow=2.3*(4/3)*Math.PI*f.size**3/(this.cell*this.cell);
    this.pile[b]+=grow*.6;
    for(const [di,dj] of [[1,0],[-1,0],[0,1],[0,-1]]){const a=i+di,c=j+dj;if(a>=0&&a<n&&c>=0&&c<n)this.pile[a*n+c]+=grow*.1;}
  }
  step(dt,heating){
    const events=[],R=this.size,g=9*R;this.clock+=dt;
    this.heat=heating?this.heat+dt*.2*(1-this.heat):this.heat-dt*.5*this.heat;
    for(const k of this.kernels){
      if(k.popped)continue;
      if(this.heat>=k.threshold&&k.readyAt<0)k.readyAt=this.clock+.6*this.random();
      if(k.readyAt>=0&&this.clock>=k.readyAt){this.pop(k);events.push('pop');}
    }
    for(const f of this.puffs){
      if(f.rest)continue;
      f.v[1]-=g*dt;for(let a=0;a<3;a++)f.p[a]+=f.v[a]*dt;f.turn+=f.spin*dt;
      const d=Math.hypot(f.p[0],f.p[2]),lidBottom=this.rimY+this.lid;
      if(!f.spilling&&d<this.inner){
        // Walls and lid keep the corn in.
        if(d>this.inner-f.size&&f.p[1]<this.rimY){const k=(this.inner-f.size)/d;f.p[0]*=k;f.p[2]*=k;f.v[0]*=-.4;f.v[2]*=-.4;}
        if(f.v[1]>0&&f.p[1]>lidBottom-f.size*.5){f.p[1]=lidBottom-f.size*.5;f.v[1]*=-.3;this.lidSpeed+=.35*R;events.push('lid');}
        const h=this.pileAt(f.p[0],f.p[2]);
        if(f.v[1]<=0&&f.p[1]-f.size*.7<=h){
          if(h+f.size*.5>this.limit(d)){
            // Full here: it rolls over the rim.
            const out=d>1e-6?[f.p[0]/d,f.p[2]/d]:[1,0];f.spilling=true;f.p[1]=h+f.size*.7;
            f.v=[out[0]*R*1.1,R*.7,out[1]*R*1.1];
          }else{this.settle(f,h);events.push('land');}
        }
      }else{
        // Over the rim and outside the pot: fall to the floor beside it.
        if(f.p[1]<this.rimY+f.size&&d<this.outer+f.size){const k=(this.outer+f.size)/Math.max(d,1e-6);f.p[0]*=k;f.p[2]*=k;}
        if(f.p[1]<=f.size*.7){f.p[1]=f.size*.7;f.v=[0,0,0];f.rest=true;f.spilling=false;events.push('land');}
      }
    }
    // The lid falls back onto the dome of popcorn under it.
    // It rests on the tops of the puffs actually lying in the pot.
    let top=this.rimY;for(const f of this.puffs)if(f.rest&&Math.hypot(f.p[0],f.p[2])<this.inner)top=Math.max(top,f.p[1]+f.size*.85);
    const rest=top-this.rimY;
    this.lidSpeed-=g*.6*dt;this.lid+=this.lidSpeed*dt;
    if(this.lid<=rest){this.lid=rest;this.lidSpeed=Math.max(0,this.lidSpeed);}
    this.lid=Math.min(this.lid,rest+R*.25);
    return events;
  }
  get busy(){return this.heat>.02||this.puffs.some(f=>!f.rest)||this.lidSpeed>1e-4;}
}
